/* eslint-disable react/require-default-props */
import React from 'react';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import './RoleImpact.css';

export type ImpactLevel = 'low' | 'medium' | 'high';

interface RoleImpactProps {
  impactLevel: ImpactLevel;
  showImpactText?: boolean;
  iconSize?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Helper function to get the appropriate icon kind for impact level
 */
const getImpactIconKind = (level: ImpactLevel): IconKind => {
  switch (level) {
    case 'high':
      return 'TrendUp';
    case 'medium':
      return 'TrendNeutral';
    case 'low':
      return 'TrendDown';
    default:
      return 'TrendUp';
  }
};

/**
 * Helper function to format impact text with proper capitalization
 */
const formatImpactText = (level: ImpactLevel): string =>
  `${level.charAt(0).toUpperCase() + level.slice(1)} Impact`;

/**
 * RoleImpact component for displaying role impact level with icon and optional text
 *
 * @param props - The component props
 * @returns JSX element containing the role impact indicator
 */
function RoleImpact({
  impactLevel,
  showImpactText = true,
  iconSize = 'small',
  className,
}: RoleImpactProps) {
  const iconKind = getImpactIconKind(impactLevel);
  const impactText = formatImpactText(impactLevel);

  return (
    <Box
      className={cx(
        'Roles-RoleImpact',
        `Roles-RoleImpact--${impactLevel}`,
        className
      )}
      role='img'
      aria-label={showImpactText ? undefined : impactText}>
      <Icon
        kind={iconKind}
        size={iconSize}
        className={cx(
          'Roles-RoleImpact-icon',
          `Roles-RoleImpact-icon--${impactLevel}`
        )}
      />
      {showImpactText && (
        <Typography
          kind='body3'
          className={cx(
            'Roles-RoleImpact-text',
            `Roles-RoleImpact-text--${impactLevel}`
          )}>
          {impactText}
        </Typography>
      )}
    </Box>
  );
}

export default RoleImpact;
